<!--
  COD6 · Compensi driver — pagina di periodo:
  filtro agenzia + mese, KPI, tabella per driver con riga espandibile e
  "Composizione compenso".

  Sorgente: GET /journey/driver-compensation?year=&month=, che restituisce una
  riga per driver con hours, compenso, km_jolly, rimborso_km, compenso_notturno,
  compenso_notturno_manuale e totale. La composizione è quindi reale, calcolata
  dal backend (ore × tariffa oraria dell'agenzia).

  Il calcolo copre spostamenti interni, PICKUP, TRANSFER e CONSEGNE — le ore
  di ogni gamba stanno sui planner — e i MINUTI AGGIUNTIVI decisi
  all'assegnazione di una tratta.

  LIQUIDAZIONE: POST /journey/driver-compensation/liquidate congela il mese di
  un driver nel registro `remuneration`. Una riga liquidata non si ricalcola
  più: se l'agenzia cambia tariffa, i mesi già pagati restano come erano.

  ⚠️ RESTA FUORI "BOOKING SVOLTI": l'endpoint dà solo aggregati per driver, e
  `/route` è esposto senza join, quindi non è filtrabile per driver. E le ore
  sono quelle PIANIFICATE — l'app driver registra lo stato della corsa, non
  l'ora in cui è stata davvero svolta.
-->
<template>
  <div
    v-loading="loading"
    class="c6"
  >
    <div class="c6__head">
      <button
        type="button"
        class="c6__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c6__title">
          <span class="c6__code">COD6</span> Compensi driver
        </h1>
        <p class="c6__sub">
          Retribuzioni di driver e jolly — base mensile
        </p>
      </div>
    </div>

    <div class="c6__filters">
      <div class="field">
        <label>Agenzia</label>
        <el-select
          v-model="agency"
          placeholder="Tutte le agenzie"
          clearable
          filterable
          style="width: 200px"
        >
          <el-option
            v-for="a in agencies"
            :key="a"
            :label="a"
            :value="a"
          />
        </el-select>
      </div>
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="seg">
        <button
          v-for="s in STATI"
          :key="s.key"
          type="button"
          class="seg__b"
          :class="{ 'is-on': stato === s.key }"
          @click="stato = s.key"
        >
          {{ s.label }}
        </button>
      </div>
      <div class="c6__sp" />
      <el-input
        v-model="search"
        placeholder="Nome driver…"
        clearable
        style="width: 210px"
      />
    </div>

    <div class="kpis">
      <div
        v-for="k in kpis"
        :key="k.label"
        class="kpi"
      >
        <div class="kpi__lbl">
          {{ k.label }}
        </div>
        <div class="kpi__num">
          {{ k.value }}
        </div>
      </div>
    </div>

    <div class="bar">
      <span class="bar__sel">
        {{ selezione.length ? `${selezione.length} da liquidare selezionati` : 'Seleziona i driver da liquidare' }}
      </span>
      <el-input
        v-model="rifFattura"
        placeholder="Rif. fattura"
        clearable
        style="width: 210px"
      />
      <el-button
        type="primary"
        :disabled="!selezione.length"
        :loading="liquidando"
        @click="liquida"
      >
        Segna come liquidato
      </el-button>
    </div>

    <div class="board">
      <el-table
        :data="filtered"
        style="width: 100%"
        row-key="driver_id"
        :expand-row-keys="expanded"
        empty-text="Nessun compenso per il periodo selezionato"
        @expand-change="onExpand"
        @selection-change="onSelection"
      >
        <el-table-column
          type="selection"
          width="46"
          :selectable="selezionabile"
        />
        <el-table-column type="expand">
          <template #default="scope">
            <div class="detail">
              <!-- Composizione: gli unici numeri realmente disponibili -->
              <section class="panel">
                <div class="panel__h">
                  Composizione compenso
                </div>
                <div class="comp">
                  <div class="comp__r">
                    <span class="comp__k">
                      Compenso base
                      <small>{{ hoursLabel(scope.row.hours) }} ×
                        {{ euro(scope.row.hourly_rate) }}/h · {{ scope.row.agency }}</small>
                    </span>
                    <span class="comp__v">{{ euro(baseOf(scope.row)) }}</span>
                  </div>
                  <!-- Gambe con un importo scritto a mano: quelle NON passano
                       dalla tariffa oraria, quindi stanno su una riga loro. -->
                  <div
                    v-if="scope.row.compenso_manuale"
                    class="comp__r"
                  >
                    <span class="comp__k">
                      Deciso a mano
                      <small>al posto della tariffa oraria, sulle gambe indicate</small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.compenso_manuale) }}</span>
                  </div>
                  <div
                    v-if="scope.row.compenso_jolly"
                    class="comp__r comp__r--sub"
                  >
                    <span class="comp__k">
                      di cui ore da jolly
                      <small>rimborso km e trasferte sono voci a parte, qui sotto</small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.compenso_jolly) }}</span>
                  </div>
                  <div class="comp__r">
                    <span class="comp__k">
                      Rimborso km jolly
                      <small v-if="scope.row.km_jolly">{{ Number(scope.row.km_jolly).toLocaleString('it-IT', { maximumFractionDigits: 1 }) }} km</small>
                    </span>
                    <span
                      class="comp__v"
                      :class="{ 'is-zero': !scope.row.rimborso_km }"
                    >{{ euro(scope.row.rimborso_km) }}</span>
                  </div>
                  <div class="comp__r">
                    <span class="comp__k">
                      Compenso notturno
                      <small>automatico + inserito a mano</small>
                    </span>
                    <span
                      class="comp__v"
                      :class="{ 'is-zero': !nightOf(scope.row) }"
                    >{{ euro(nightOf(scope.row)) }}</span>
                  </div>
                  <div
                    v-if="scope.row.compenso_extra"
                    class="comp__r"
                  >
                    <span class="comp__k">
                      Compensi aggiuntivi
                      <small>voci decise a mano, non corse</small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.compenso_extra) }}</span>
                  </div>
                  <div class="comp__r comp__r--tot">
                    <span class="comp__k">
                      Compenso del mese
                      <small>
                        {{ scope.row.corse_spostamenti || 0 }} spostamenti interni ·
                        {{ scope.row.corse_prenotazioni || 0 }} gambe di prenotazioni
                        <template v-if="scope.row.minuti_extra">
                          · {{ scope.row.minuti_extra }} min aggiuntivi
                        </template>
                      </small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.compenso_totale) }}</span>
                  </div>
                  <!-- Le trasferte sono un costo come gli altri, ma non si
                       liquidano col mese: si pagano alla nota spese, anche a
                       mese aperto. Per questo la riga dice quante ne restano. -->
                  <div
                    v-if="scope.row.trasferte"
                    class="comp__r"
                  >
                    <span class="comp__k">
                      Rimborso trasferte
                      <small>
                        {{ euro(scope.row.trasferte_pagate) }} già pagate ·
                        {{ euro(scope.row.trasferte_da_pagare) }} da pagare
                      </small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.trasferte) }}</span>
                  </div>
                  <div class="comp__r comp__r--tot">
                    <span class="comp__k">
                      Totale da pagare
                      <small>
                        {{ euro(scope.row.pagato) }} già pagato ·
                        {{ euro(scope.row.da_pagare) }} da liquidare
                      </small>
                    </span>
                    <span class="comp__v">{{ euro(scope.row.totale) }}</span>
                  </div>
                  <div
                    v-if="scope.row.liquidato"
                    class="comp__r comp__r--liq"
                  >
                    <span class="comp__k">
                      Liquidato
                      <small>{{ rifLabel(scope.row) }}</small>
                    </span>
                    <span class="comp__v">importi congelati</span>
                  </div>
                </div>
              </section>

              <!-- Compensi aggiuntivi: importi che non nascono da una corsa
                   (un rimborso concordato, una correzione) e che si sommano al
                   totale del mese. Una voce per riga, con la sua descrizione:
                   a fine mese si deve poter dire di cosa era fatto il totale. -->
              <section class="panel">
                <div class="panel__h">
                  Compensi aggiuntivi
                </div>
                <div class="comp">
                  <div
                    v-for="e in extrasOf(scope.row)"
                    :key="e.id"
                    class="comp__r"
                  >
                    <span class="comp__k">
                      {{ e.descrizione || 'Compenso aggiuntivo' }}
                    </span>
                    <span class="comp__v">{{ euro(e.importo) }}</span>
                    <el-button
                      size="small"
                      text
                      @click="togliExtra(scope.row, e)"
                    >
                      Togli
                    </el-button>
                  </div>
                  <p
                    v-if="!extrasOf(scope.row).length"
                    class="panel__empty"
                  >
                    Nessun compenso aggiuntivo in {{ periodLabel.toLowerCase() }}.
                  </p>

                  <div class="extra__form">
                    <el-input
                      v-model="bozzaExtra(scope.row.driver_id).descrizione"
                      size="small"
                      placeholder="Di cosa si tratta"
                      class="extra__desc"
                    />
                    <el-input
                      v-model="bozzaExtra(scope.row.driver_id).importo"
                      size="small"
                      placeholder="0,00"
                      class="extra__imp"
                    >
                      <template #prepend>
                        €
                      </template>
                    </el-input>
                    <el-button
                      size="small"
                      type="primary"
                      :loading="extraSaving === scope.row.driver_id"
                      @click="aggiungiExtra(scope.row)"
                    >
                      Aggiungi
                    </el-button>
                  </div>
                  <p
                    v-if="scope.row.liquidato"
                    class="panel__empty"
                  >
                    Mese già liquidato: una voce aggiunta ora non cambia l'importo pagato.
                  </p>
                </div>
              </section>

              <!-- Le corse che compongono il compenso: stesso calcolo dei
                   totali, quindi la somma torna. Si chiedono all'apertura
                   della riga, non per tutti i driver del mese. -->
              <section class="panel panel--wide">
                <div class="panel__h">
                  Corse svolte
                </div>
                <p
                  v-if="ridesOf(scope.row).loading"
                  class="panel__empty"
                >
                  Caricamento…
                </p>
                <p
                  v-else-if="ridesOf(scope.row).error"
                  class="panel__empty"
                >
                  {{ ridesOf(scope.row).error }}
                </p>
                <p
                  v-else-if="!ridesOf(scope.row).rides.length"
                  class="panel__empty"
                >
                  Nessuna corsa nel mese.
                </p>
                <div
                  v-else
                  class="rides"
                >
                  <!-- Una corsa: in alto quanto vale, sotto le voci che la
                       compongono, tutte modificabili dove il server lo
                       consente. `@click.stop` sulla fascia delle voci: la
                       riga apre la corsa, i campi no. -->
                  <article
                    v-for="r in ridesOf(scope.row).rides"
                    :key="chiaveCorsa(r)"
                    class="ride"
                  >
                    <header
                      class="ride__hd"
                      :class="{ 'is-clickable': r.journey_id || r.booking_id }"
                      @click="openRide(r)"
                    >
                      <span class="ride__when">{{ dayLabel(r.date_start) }}</span>
                      <span class="ride__what">
                        {{ r.label }}
                        <!-- Da dove a dove: il nome della sede quando il capo
                             è una sede, altrimenti l'indirizzo. -->
                        <small
                          v-if="r.da || r.a"
                          class="ride__tratta"
                        >{{ r.da || '—' }} → {{ r.a || '—' }}</small>
                        <small>
                          <!-- Il passeggero rientra a bordo di una corsa a
                               catena (fino a 4 per auto), pagato come un
                               driver. Compenso, minuti e trasferta si scrivono
                               come per gli altri; il rimborso km no (non
                               viaggia con un mezzo suo). -->
                          {{ r.passeggero ? 'passeggero' : r.role === 'jolly' ? 'jolly' : 'conducente' }}
                          <!-- Il jolly che segue più auto nello stesso
                               viaggio: una corsa sola, pagata una volta. -->
                          <template v-if="r.auto > 1">· segue {{ r.auto }} auto</template>
                          <template v-if="r.dettaglio">· {{ r.dettaglio }}</template>
                          <template v-if="r.km">· {{ r.km }} km</template>
                          <template v-if="r.compenso_notturno">
                            · notturno {{ euro(r.compenso_notturno) }}
                          </template>
                        </small>
                      </span>
                      <span class="ride__hours">{{ hoursLabel(r.hours) }}</span>
                      <span
                        class="ride__tot"
                        :class="{ 'is-draft': totaleCorsa(scope.row, r) !== r.totale }"
                      >{{ euro(totaleCorsa(scope.row, r)) }}</span>
                    </header>

                    <!-- Le voci della corsa, sempre nelle stesse colonne:
                         compenso, minuti aggiuntivi, rimborso km, trasferta,
                         compenso aggiuntivo. Le celle che non valgono per
                         questa corsa restano vuote, così le corse restano
                         incolonnate fra loro. -->
                    <div
                      class="ride__voci"
                      @click.stop
                    >
                      <!-- COMPENSO: nasce dal calcolo (ore × tariffa oraria)
                           e si può correggere. Svuotare il campo torna al
                           calcolo. -->
                      <label class="voce">
                        <span class="voce__lbl">
                          Compenso
                          <template v-if="!r.manuale">· calcolato</template>
                        </span>
                        <el-input
                          v-model="bozzaVoci(r).compenso"
                          size="small"
                          placeholder="ore × tariffa"
                          class="voce__in"
                          :aria-label="`Compenso per ${r.label}`"
                          :disabled="!r.ride_id"
                          @keyup.enter="salvaVoci(scope.row, r, 'compenso')"
                          @blur="salvaVoci(scope.row, r, 'compenso')"
                        >
                          <template #prepend>
                            €
                          </template>
                        </el-input>
                      </label>

                      <!-- MINUTI AGGIUNTIVI: attese e soste, solo sulle
                           tratte degli spostamenti interni. -->
                      <label
                        v-if="r.kind === 'route' || r.kind === 'passenger'"
                        class="voce"
                      >
                        <span class="voce__lbl">Min. agg.</span>
                        <el-input
                          v-model="bozzaVoci(r).minuti"
                          size="small"
                          placeholder="0"
                          class="voce__in"
                          :aria-label="`Minuti aggiuntivi per ${r.label}`"
                          :disabled="!r.ride_id || !r.journey_id"
                          @keyup.enter="salvaVoci(scope.row, r, 'minuti')"
                          @blur="salvaVoci(scope.row, r, 'minuti')"
                        >
                          <template #append>
                            min
                          </template>
                        </el-input>
                      </label>
                      <span
                        v-else
                        class="voce voce--vuota"
                      />

                      <!-- RIMBORSO KM: lo prende solo il jolly, non il
                           passeggero (rientra a bordo, non con un mezzo suo).
                           Svuotare il campo torna al calcolo km × tariffa. -->
                      <label
                        v-if="r.role === 'jolly' && !r.passeggero"
                        class="voce"
                      >
                        <span class="voce__lbl">
                          Rimb. km
                          <template v-if="!r.rimborso_manuale">· calcolato</template>
                        </span>
                        <el-input
                          v-model="bozzaVoci(r).rimborso"
                          size="small"
                          placeholder="km × tariffa"
                          class="voce__in"
                          :aria-label="`Rimborso km per ${r.label}`"
                          :disabled="!r.ride_id"
                          @keyup.enter="salvaVoci(scope.row, r, 'rimborso')"
                          @blur="salvaVoci(scope.row, r, 'rimborso')"
                        >
                          <template #prepend>
                            €
                          </template>
                        </el-input>
                      </label>
                      <span
                        v-else
                        class="voce voce--vuota"
                      />

                      <!-- TRASFERTA: si scrive e si spunta pagata quando la si
                           liquida con la nota spese, anche a mese aperto. Vale
                           anche per il passeggero. -->
                      <label class="voce">
                        <span class="voce__lbl">Trasferta</span>
                        <el-input
                          v-model="bozzaVoci(r).trasferta"
                          size="small"
                          placeholder="0,00"
                          class="voce__in"
                          :aria-label="`Rimborso trasferta per ${r.label}`"
                          :disabled="!r.ride_id"
                          @keyup.enter="salvaVoci(scope.row, r, 'trasferta')"
                          @blur="salvaVoci(scope.row, r, 'trasferta')"
                        >
                          <template #prepend>
                            €
                          </template>
                        </el-input>
                      </label>
                      <el-checkbox
                        v-model="bozzaVoci(r).pagata"
                        class="voce__pagata"
                        :disabled="!r.ride_id || !bozzaVoci(r).trasferta"
                        @change="salvaVoci(scope.row, r, 'trasferta')"
                      >
                        pagata
                      </el-checkbox>

                      <!-- COMPENSO AGGIUNTIVO su questa corsa: una voce del
                           pannello qui sopra, già intestata alla corsa. -->
                      <label class="voce voce--add">
                        <span class="voce__lbl">Compenso agg.</span>
                        <span class="voce__addRow">
                          <el-input
                            v-model="rideExtra[chiaveCorsa(r)]"
                            size="small"
                            placeholder="+ €"
                            class="voce__in voce__in--add"
                            :aria-label="`Compenso aggiuntivo per ${r.label}`"
                            @keyup.enter="aggiungiExtraCorsa(scope.row, r)"
                          />
                          <el-button
                            size="small"
                            :loading="extraSaving === scope.row.driver_id"
                            @click="aggiungiExtraCorsa(scope.row, r)"
                          >
                            Aggiungi
                          </el-button>
                        </span>
                      </label>
                    </div>
                  </article>

                  <div class="ride ride--tot">
                    <header class="ride__hd">
                      <span class="ride__when" />
                      <span class="ride__what">
                        {{ ridesOf(scope.row).rides.length }} corse
                        <small>
                          trasferte {{ euro(ridesOf(scope.row).trasferte) }}
                          <template v-if="ridesOf(scope.row).trasferteDaPagare">
                            · {{ euro(ridesOf(scope.row).trasferteDaPagare) }} da pagare
                          </template>
                          <template v-if="ridesOf(scope.row).liquidato">
                            · mese liquidato: il totale pagato è quello congelato nel registro
                          </template>
                        </small>
                      </span>
                      <span class="ride__hours" />
                      <span class="ride__tot">{{ euro(ridesTotal(scope.row)) }}</span>
                    </header>
                  </div>
                </div>
              </section>

              <div class="detail__actions">
                <el-button
                  size="small"
                  @click="openDriver(scope.row)"
                >
                  Apri scheda driver
                </el-button>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Driver"
          min-width="200"
        >
          <template #default="scope">
            <div class="drv">
              <span class="ava">{{ ini(scope.row.driver) }}</span>
              <div>
                <div class="drv__nm">
                  {{ scope.row.driver || '—' }}
                </div>
                <div class="drv__ag">
                  {{ scope.row.agency }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Ore"
          width="112"
          align="right"
        >
          <template #default="scope">
            <span class="num">{{ hoursLabel(scope.row.hours) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Tariffa"
          width="112"
          align="right"
        >
          <template #default="scope">
            <span class="num">{{ euro(scope.row.hourly_rate) }}/h</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Rimb. km"
          width="126"
          align="right"
        >
          <template #default="scope">
            <span class="num val">{{ euro(scope.row.rimborso_km) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Trasferte"
          width="126"
          align="right"
        >
          <template #default="scope">
            <span class="num val">{{ euro(scope.row.trasferte) }}</span>
            <small
              v-if="scope.row.trasferte_da_pagare"
              class="colsub"
            >{{ euro(scope.row.trasferte_da_pagare) }} da pagare</small>
          </template>
        </el-table-column>

        <el-table-column
          label="Totale"
          width="168"
          align="right"
        >
          <template #default="scope">
            <span class="num val val--tot">{{ euro(scope.row.totale) }}</span>
            <small
              v-if="scope.row.da_pagare && scope.row.da_pagare !== scope.row.totale"
              class="colsub"
            >{{ euro(scope.row.da_pagare) }} da liquidare</small>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="158"
        >
          <template #default="scope">
            <span
              v-if="scope.row.liquidato"
              class="tag tag--ok"
              :title="rifLabel(scope.row)"
            >Liquidato</span>
            <span
              v-else
              class="tag"
            >Da liquidare</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="board__foot">
        <span>{{ filtered.length }} driver</span>
        <span class="c6__sp" />
        <span class="board__tot">
          Totale periodo <b>{{ euro(filteredTotal) }}</b>
        </span>
      </div>
    </div>

    <p class="c6__note">
      Liquidare scrive una riga per driver e per mese in
      <code>remuneration</code>, con importi e tariffe applicate: da quel
      momento il mese non si ricalcola più.
    </p>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import journeyAPI from '../../../services/journeyAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const STATI = [
  { key: 'tutti', label: 'Tutti' },
  { key: 'da_liquidare', label: 'Da liquidare' },
  { key: 'liquidati', label: 'Liquidati' },
];

export default {
  name: 'Cod6DetailPage',

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const rows = ref([]);
    const total = ref(0);
    const liquidatoTot = ref(0);
    const daLiquidareTot = ref(0);
    // Trasferte del mese ancora da pagare: stanno fuori dal totale compensi.
    const trasferteDaPagare = ref(0);
    const loading = ref(false);
    const agency = ref('');
    const search = ref('');
    const expanded = ref([]);
    const stato = ref('tutti');
    const selezione = ref([]);
    const rifFattura = ref('');
    const liquidando = ref(false);

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    // Le agenzie escono dai dati stessi: l'endpoint restituisce già il nome.
    const agencies = computed(() =>
      [...new Set(rows.value.map((r) => r.agency).filter((a) => a && a !== '—'))].sort()
    );

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return rows.value.filter((r) => {
        if (agency.value && r.agency !== agency.value) return false;
        if (q && !(r.driver || '').toLowerCase().includes(q)) return false;
        if (stato.value === 'da_liquidare' && r.liquidato) return false;
        if (stato.value === 'liquidati' && !r.liquidato) return false;
        return true;
      });
    });

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, r) => s + Number(r.totale || 0), 0)
    );

    const nightOf = (r) =>
      Number(r.compenso_notturno || 0) + Number(r.compenso_notturno_manuale || 0);
    // `compenso` comprende anche gli importi scritti a mano: la riga "base"
    // deve mostrare solo quello che viene davvero dalle ore per tariffa.
    const baseOf = (r) => Number(r.compenso || 0) - Number(r.compenso_manuale || 0);

    const kpis = computed(() => [
      // Tutte le voci: compenso, rimborsi km, notturni e trasferte.
      { label: 'Totale del mese', value: euro(total.value) },
      { label: 'Da liquidare', value: euro(daLiquidareTot.value) },
      // Il mese congelato nel registro più le trasferte già pagate.
      { label: 'Liquidato', value: euro(liquidatoTot.value) },
      { label: 'Trasferte da pagare', value: euro(trasferteDaPagare.value) },
      {
        label: 'Ore totali',
        value: hoursLabel(rows.value.reduce((s, r) => s + Number(r.hours || 0), 0)),
      },
      { label: 'Driver', value: rows.value.length },
    ]);

    // `keepExpanded`: un salvataggio dalla riga aperta ricarica i numeri senza
    // richiudere il dettaglio che si sta usando.
    const load = async ({ keepExpanded = false } = {}) => {
      loading.value = true;
      if (!keepExpanded) {
        expanded.value = [];
        selezione.value = [];
      }
      try {
        const res = await contabilitaAPI.cod6Rows(year.value, month.value);
        rows.value = res.rows;
        total.value = res.total;
        liquidatoTot.value = res.liquidato;
        daLiquidareTot.value = res.daLiquidare;
        trasferteDaPagare.value = res.trasferteDaPagare;
      } catch (e) {
        console.error('Caricamento compensi fallito:', e);
        rows.value = [];
        total.value = 0;
        liquidatoTot.value = 0;
        daLiquidareTot.value = 0;
        trasferteDaPagare.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // Si liquida la parte di COMPENSO del mese: le trasferte si pagano alla
    // nota spese, segnandole sulla corsa. Chi ha solo trasferte non ha un mese
    // da congelare.
    const selezionabile = (row) => !row.liquidato && Number(row.compenso_totale || 0) > 0;
    const onSelection = (righe) => {
      selezione.value = righe.filter(selezionabile);
    };

    const rifLabel = (r) => {
      const quando = r.liquidato_il
        ? new Date(r.liquidato_il).toLocaleDateString('it-IT')
        : null;
      const rif = r.rif_fattura ? `rif. ${r.rif_fattura}` : null;
      return [quando, rif].filter(Boolean).join(' · ') || 'liquidato';
    };

    const liquida = async () => {
      if (!selezione.value.length) return;
      // `rif_fattura` è testo: «2026/0001» si salva com'è.
      const rif = rifFattura.value.trim();
      // La liquidazione congela il mese e non si annulla dall'applicazione.
      const quanti = selezione.value.length;
      try {
        await ElMessageBox.confirm(
          `${quanti} ${quanti === 1 ? 'driver verrà liquidato' : 'driver verranno liquidati'}` +
            (rif ? ` con il riferimento "${rif}"` : ' senza riferimento fattura') +
            '. Da quel momento il mese non si ricalcola più.',
          'Confermi la liquidazione?',
          { confirmButtonText: 'Segna come liquidato', cancelButtonText: 'Annulla' }
        );
      } catch {
        return;
      }
      liquidando.value = true;
      try {
        const res = await contabilitaAPI.cod6Liquidate(
          year.value,
          month.value,
          selezione.value.map((r) => r.driver_id),
          rif || null
        );
        const saltati = res.saltati?.length
          ? `, ${res.saltati.length} saltati (già liquidati o senza compenso)`
          : '';
        ElMessage.success(
          `${res.liquidate} driver liquidati per ${euro(res.totale)}${saltati}`
        );
        rifFattura.value = '';
        await load();
      } catch (e) {
        console.error('Liquidazione compensi fallita:', e);
        ElMessage.error('Liquidazione non riuscita: riprova');
      } finally {
        liquidando.value = false;
      }
    };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    // Le ore arrivano decimali: si mostrano come "6h 30m", che è come le legge
    // chi controlla un compenso.
    function hoursLabel(h) {
      const v = Number(h || 0);
      const hh = Math.floor(v);
      const mm = Math.round((v - hh) * 60);
      return `${hh}h ${String(mm).padStart(2, '0')}m`;
    }

    // Giorno e ora della corsa: nel dettaglio di un mese l'anno è superfluo.
    function dayLabel(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${giorno} · ${hh}:${mm}`;
    }

    const ini = (name) => {
      const parts = (name || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '?';
      return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
    };

    // Corse per driver, caricate all'apertura della riga e tenute in cache:
    // { [driverId]: { rides, totale, liquidato, loading, error } }.
    const rides = reactive({});
    const VUOTO = {
      rides: [], totale: 0, liquidato: false, loading: false, error: '',
      trasferte: 0, trasfertePagate: 0, trasferteDaPagare: 0,
    };
    const ridesOf = (row) => rides[row.driver_id] ?? VUOTO;
    // Il totale del mese liquidato è quello congelato: la somma delle corse è
    // il calcolo, e le due cose possono non coincidere. Si mostra il pagato.
    // Totale di UNA corsa con quello che c'è scritto ORA nei campi, prima
    // ancora di salvare: il numero a destra deve seguire quello che si sta
    // scrivendo, altrimenti si corregge un importo al buio.
    //
    // Stesse regole del server (`paga` in journey.service): il compenso è
    // ore × tariffa oraria e le ore comprendono già i minuti aggiuntivi; un
    // compenso scritto a mano sostituisce il calcolo e, con esso, il
    // supplemento notturno. Rimborso km e trasferta si sommano a parte.
    const numeroScritto = (testo) => {
      const grezzo = String(testo ?? '').replace(',', '.').trim();
      if (grezzo === '') return null;
      const n = Number(grezzo);
      return Number.isFinite(n) && n >= 0 ? n : null;
    };

    const totaleCorsa = (row, r) => {
      const bozza = bozzaVoci(r);
      const tariffa = Number(row.hourly_rate) || 0;
      const manuale = numeroScritto(bozza.compenso);
      // ore senza i minuti aggiuntivi salvati, più quelli scritti adesso
      const oreBase = Math.max(0, Number(r.hours || 0) - (Number(r.extra_min || 0) / 60));
      const minuti = r.kind === 'route' ? (numeroScritto(bozza.minuti) ?? 0) : Number(r.extra_min || 0);
      const compenso = manuale !== null ? manuale : (oreBase + minuti / 60) * tariffa;
      const notturno = manuale !== null ? 0 : Number(r.compenso_notturno || 0);
      const rimborso = r.role === 'jolly' && !r.passeggero
        ? (numeroScritto(bozza.rimborso) ?? (Number(row.km_refund_rate) || 0) * Number(r.km || 0))
        : Number(r.rimborso_km || 0);
      const trasferta = numeroScritto(bozza.trasferta) ?? 0;
      return Math.round((compenso + notturno + rimborso + trasferta + Number.EPSILON) * 100) / 100;
    };

    const ridesTotal = (row) => (ridesOf(row).liquidato
      ? Number(row.totale || 0)
      : ridesOf(row).rides.reduce((s, r) => s + totaleCorsa(row, r), 0));

    const loadRides = async (driverId, { force = false } = {}) => {
      if (!driverId || (rides[driverId] && !force)) return;
      rides[driverId] = { ...VUOTO, loading: true };
      try {
        const res = await contabilitaAPI.cod6DriverRides(driverId, year.value, month.value);
        rides[driverId] = { ...res, loading: false, error: '' };
        sincronizzaVoci(res.rides);
      } catch (e) {
        console.error('Corse del driver non caricate:', e);
        rides[driverId] = { ...VUOTO, error: 'Corse non caricate: riprova.' };
      }
    };

    // Compensi aggiuntivi per driver: { [driverId]: [voci] }, più la bozza del
    // form. Si caricano insieme alle corse, all'apertura della riga.
    const extras = reactive({});
    const extraDraft = reactive({});
    const extraSaving = ref('');
    const extrasOf = (row) => extras[row.driver_id] || [];

    const bozzaExtra = (driverId) => {
      if (!extraDraft[driverId]) extraDraft[driverId] = { descrizione: '', importo: '' };
      return extraDraft[driverId];
    };

    const loadExtras = async (driverId) => {
      bozzaExtra(driverId);
      try {
        extras[driverId] = await contabilitaAPI.cod6Extras(year.value, month.value, driverId);
      } catch (e) {
        console.error('Compensi aggiuntivi non letti:', e);
        extras[driverId] = [];
      }
    };

    // Compenso aggiuntivo intestato a UNA corsa: stessa voce del pannello
    // «Compensi aggiuntivi», con la descrizione già scritta.
    const rideExtra = reactive({});
    // Una corsa è la sua riga a DB (tratta, gamba o passeggero) e il ruolo:
    // sulla stessa tratta stanno conducente e jolly. Spostamento, giorno e
    // ruolo non bastano: tre auto che partono insieme avevano la stessa
    // chiave, e la modifica di una finiva sulle altre.
    const chiaveCorsa = (r) => r.ride_id
      ? `${r.kind}-${r.ride_id}-${r.role}`
      : `${r.origine}-${r.date_start}-${r.role}-${r.journey_id || r.booking_id}`;

    const aggiungiExtraCorsa = async (row, r) => {
      const chiave = chiaveCorsa(r);
      const importo = Number(String(rideExtra[chiave] ?? '').replace(',', '.').trim());
      if (!Number.isFinite(importo) || importo <= 0) {
        ElMessage.error('Scrivi un importo maggiore di zero');
        return;
      }
      extraSaving.value = row.driver_id;
      try {
        await contabilitaAPI.cod6AddExtra({
          driverId: row.driver_id,
          year: year.value,
          month: month.value,
          importo,
          descrizione: `${r.label} del ${dayLabel(r.date_start)}`,
        });
        rideExtra[chiave] = '';
        await Promise.all([loadExtras(row.driver_id), load()]);
        ElMessage.success('Compenso aggiunto alla corsa');
      } catch (e) {
        console.error('Compenso non aggiunto:', e);
        ElMessage.error('Compenso non aggiunto: riprova.');
      } finally {
        extraSaving.value = '';
      }
    };

    const aggiungiExtra = async (row) => {
      const bozza = bozzaExtra(row.driver_id);
      const importo = Number(String(bozza.importo ?? '').replace(',', '.').trim());
      if (!Number.isFinite(importo) || importo <= 0) {
        ElMessage.error('Scrivi un importo maggiore di zero');
        return;
      }
      extraSaving.value = row.driver_id;
      try {
        await contabilitaAPI.cod6AddExtra({
          driverId: row.driver_id,
          year: year.value,
          month: month.value,
          importo,
          descrizione: bozza.descrizione,
        });
        bozza.descrizione = '';
        bozza.importo = '';
        await Promise.all([loadExtras(row.driver_id), load()]);
        ElMessage.success('Compenso aggiunto');
      } catch (e) {
        console.error('Compenso non aggiunto:', e);
        ElMessage.error('Compenso non aggiunto: riprova.');
      } finally {
        extraSaving.value = '';
      }
    };

    const togliExtra = async (row, voce) => {
      try {
        await contabilitaAPI.cod6RemoveExtra(voce.id);
        await Promise.all([loadExtras(row.driver_id), load()]);
      } catch (e) {
        console.error('Compenso non tolto:', e);
        ElMessage.error('Compenso non tolto: riprova.');
      }
    };

    // --- Voci decise a mano sulla corsa -----------------------------------
    // Bozza per corsa: { [chiave]: { trasferta, pagata, rimborso } },
    // riallineata a quello che il server ha salvato ogni volta che le corse si
    // ricaricano. Il rimborso km si scrive solo se è stato deciso a mano:
    // altrimenti il campo resta vuoto e mostra il calcolo come segnaposto.
    const vociDraft = reactive({});
    const importoLeggibile = (v) => (v || v === 0 ? String(v).replace('.', ',') : '');
    const vociDaCorsa = (r) => ({
      trasferta: importoLeggibile(r.trasferta),
      pagata: !!r.trasferta_pagata,
      // Gli importi si mostrano SCRITTI NEL CAMPO, anche quando sono il
      // calcolo: un campo vuoto col solo segnaposto sembrava non modificabile.
      // Svuotarlo torna al calcolo (vedi salvaVoci).
      compenso: importoLeggibile(r.compenso),
      minuti: r.extra_min ? String(r.extra_min) : '',
      rimborso: importoLeggibile(r.rimborso_km),
    });
    const sincronizzaVoci = (lista) => {
      for (const r of lista || []) vociDraft[chiaveCorsa(r)] = vociDaCorsa(r);
    };
    const bozzaVoci = (r) => {
      const chiave = chiaveCorsa(r);
      if (!vociDraft[chiave]) vociDraft[chiave] = vociDaCorsa(r);
      return vociDraft[chiave];
    };

    // Un campo vuoto vale «nessun importo»: null, che sul server cancella la
    // trasferta e riporta il rimborso km al calcolo.
    const importoScritto = (testo) => {
      const grezzo = String(testo ?? '').replace(',', '.').trim();
      if (grezzo === '') return null;
      const n = Number(grezzo);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };

    // Dopo una modifica su una corsa: le corse del driver e i totali del mese.
    const ricaricaCorse = async (row) => {
      await Promise.all([
        loadRides(row.driver_id, { force: true }),
        load({ keepExpanded: true }),
      ]);
    };

    // I campi degli importi nascono col valore che vale ORA, anche quando è
    // il calcolo automatico. Non c'è niente da salvare in due casi: l'importo
    // a mano non è cambiato, oppure il campo mostra ancora il calcolo e nessuno
    // l'ha toccato — altrimenti il solo aprire e chiudere una riga
    // congelerebbe a mano il calcolo di ogni corsa.
    const nonCambiato = (scritto, eraAMano, valoreOra) => {
      if (eraAMano) return Number(valoreOra) === scritto;
      if (scritto === null) return true;
      return Math.abs(scritto - Number(valoreOra || 0)) < 0.005;
    };

    const salvaVoci = async (row, r, campo) => {
      // Le corse di un mese liquidato restano modificabili: la liquidazione
      // congela il compenso del mese, non queste voci.
      if (!r.ride_id) return;
      const bozza = bozzaVoci(r);
      const patch = {};
      if (campo === 'trasferta') {
        const importo = importoScritto(bozza.trasferta);
        if (importo === undefined) {
          ElMessage.error('Scrivi un rimborso trasferta valido');
          return;
        }
        const invariato =
          (Number(r.trasferta) || 0) === (importo ?? 0) && !!r.trasferta_pagata === !!bozza.pagata;
        if (invariato) return;
        patch.trasferta = importo;
        patch.pagata = importo === null ? false : bozza.pagata;
      } else if (campo === 'compenso') {
        // Vale per tutte le corse — tratte e gambe: svuotare il campo
        // (`null`) torna al calcolo ore × tariffa.
        const importo = importoScritto(bozza.compenso);
        if (importo === undefined) {
          ElMessage.error('Scrivi un compenso valido');
          return;
        }
        if (nonCambiato(importo, r.manuale, r.compenso)) return;
        patch.compenso = importo;
      } else if (campo === 'minuti') {
        // Minuti aggiuntivi della TRATTA: si salvano con l'assegnazione. I
        // driver non si toccano (campo assente = invariato), quindi nessuna
        // notifica parte.
        const testo = String(bozza.minuti ?? '').trim();
        const min = testo === '' ? 0 : Number(testo.replace(',', '.'));
        if (!Number.isFinite(min) || min < 0) {
          ElMessage.error('Scrivi i minuti aggiuntivi come numero');
          return;
        }
        const minuti = Math.round(min);
        if (minuti === (Number(r.extra_min) || 0)) return;
        // Il passeggero ha i suoi minuti sulla sua riga, non sulla tratta.
        if (r.kind === 'passenger') {
          try {
            await contabilitaAPI.cod6SetVociCorsa({ kind: r.kind, id: r.ride_id, minuti });
            await ricaricaCorse(row);
            ElMessage.success('Salvato');
          } catch (e) {
            console.error('Minuti aggiuntivi non salvati:', e);
            ElMessage.error('Non salvati: riprova.');
            await loadRides(row.driver_id, { force: true });
          }
          return;
        }
        try {
          await journeyAPI.assignDrivers(r.journey_id, {
            assignments: [{
              routeId: r.ride_id,
              ...(r.role === 'jolly' ? { extraMinJolly: minuti } : { extraMinDriver: minuti }),
            }],
          });
          await ricaricaCorse(row);
          ElMessage.success('Salvato');
        } catch (e) {
          console.error('Minuti aggiuntivi non salvati:', e);
          ElMessage.error('Non salvati: riprova.');
          await loadRides(row.driver_id, { force: true });
        }
        return;
      } else {
        const importo = importoScritto(bozza.rimborso);
        if (importo === undefined) {
          ElMessage.error('Scrivi un rimborso km valido');
          return;
        }
        if (nonCambiato(importo, r.rimborso_manuale, r.rimborso_km)) return;
        patch.rimborsoKm = importo;
      }

      try {
        await contabilitaAPI.cod6SetVociCorsa({ kind: r.kind, id: r.ride_id, role: r.role, ...patch });
        await ricaricaCorse(row);
        ElMessage.success('Salvato');
      } catch (e) {
        console.error('Voce della corsa non salvata:', e);
        ElMessage.error('Non salvato: riprova.');
        await loadRides(row.driver_id, { force: true });
      }
    };

    const openRide = (r) => {
      if (r.journey_id) router.push(`/journey/detail/${r.journey_id}`);
      else if (r.booking_id) router.push(`/booking/detail/${r.booking_id}`);
    };

    const onExpand = (row, rowsExpanded) => {
      expanded.value = rowsExpanded.map((r) => r.driver_id);
      if (expanded.value.includes(row.driver_id)) {
        loadRides(row.driver_id);
        loadExtras(row.driver_id);
      }
    };

    const openDriver = (r) => {
      if (r?.driver_id) router.push(`/driver/detail/${r.driver_id}`);
    };

    const goBack = () => router.push('/contabilita');

    onMounted(load);

    return {
      year, month, rows, loading, agency, search, expanded, agencies,
      filtered, filteredTotal, kpis, periodLabel,
      STATI, stato, selezione, rifFattura, liquidando,
      shiftMonth, euro, hoursLabel, ini, nightOf, baseOf, onExpand, openDriver, goBack,
      ridesOf, ridesTotal, totaleCorsa, openRide, dayLabel,
      bozzaVoci, salvaVoci,
      extrasOf, bozzaExtra, extraSaving, aggiungiExtra, togliExtra,
      rideExtra, chiaveCorsa, aggiungiExtraCorsa,
      selezionabile, onSelection, rifLabel, liquida,
    };
  },
};
</script>

<style scoped>
.c6 { padding: 4px 0 32px; }

.extra__form { display: flex; align-items: center; gap: 8px; padding-top: 8px; }
.extra__desc { flex: 1; min-width: 0; }
.extra__imp { width: 130px; flex: none; }

/* ---- corse del driver (pannello di dettaglio) ----
   Griglia invece di flex: la descrizione è l'unica colonna elastica
   (minmax(0,1fr), altrimenti un partner dal nome lungo allarga la riga e
   spinge fuori gli importi), le altre tre si dimensionano sul contenuto. */
/* Le corse non si allargano oltre le loro voci: su uno schermo grande ore e
   totale finivano all'estremo destro, lontanissimi dalla descrizione. */
.rides { padding: 6px 13px 12px; max-width: 1040px; }
/* Una corsa: intestazione (quando, cosa, ore, totale) e sotto la fascia
   delle voci. Prima era tutto su una griglia sola e i campi finivano fuori
   colonna appena una voce mancava. */
.ride {
  border-bottom: 1px solid var(--border-color); padding: 9px 0;
}
.ride:last-child { border-bottom: 0; }
.ride__hd {
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: baseline; gap: 4px 12px;
}
.ride__hd.is-clickable { cursor: pointer; }
.ride__hd.is-clickable:hover { background: var(--bg-secondary); }
.ride__when {
  font-variant-numeric: tabular-nums; color: var(--text-tertiary); white-space: nowrap;
}
.ride__what { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ride__what small { color: var(--text-tertiary); font-size: 11.5px; line-height: 1.4; }
.ride__what .ride__tratta { color: var(--text-secondary); }
.ride__hours {
  text-align: right; color: var(--text-tertiary); white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ride__tot {
  text-align: right; font-weight: 650; white-space: nowrap;
  font-variant-numeric: tabular-nums; min-width: 72px;
}
/* Le voci: COLONNE FISSE, sempre le stesse. Una fila che va a capo faceva
   scivolare i campi di una corsa rispetto a quelli di quella sopra — bastava
   che una voce non ci fosse — e la fascia sembrava sfilacciata. Le celle che
   non valgono per una corsa restano vuote e tengono il posto. */
.ride__voci {
  display: grid;
  /* `justify-content: start`: le colonne restano attaccate a sinistra invece
     di allargarsi per riempire il pannello, che lasciava un vuoto in mezzo. */
  grid-template-columns: 116px 92px 116px 116px 80px auto;
  justify-content: start; align-items: end; gap: 8px 10px; margin-top: 8px;
}
/* Il totale mentre lo si modifica: è quello che verrà salvato, non ancora
   quello del server. */
.ride__tot.is-draft { color: var(--state-active); }
.voce { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.voce__lbl {
  color: var(--text-tertiary); font-size: 11px; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.voce__in { width: 100%; }
.voce__in--add { width: 88px; }
.voce--vuota { min-height: 1px; }
/* La casella di Element Plus ha 30px di margine a destra e l'altezza dei
   campi normali: nella sua colonna sbordava sul «Compenso agg.» e restava
   più in basso dei campi piccoli. Qui ha l'altezza dei campi piccoli e
   niente margine. */
.voce__pagata.el-checkbox { height: 24px; margin: 0; justify-self: start; }
/* Il compenso aggiuntivo è un'azione a sé: staccato dalle voci della corsa. */
.voce--add { margin-left: 8px; padding-left: 14px; border-left: 1px solid var(--border-color); }
.voce__addRow { display: flex; align-items: center; gap: 6px; }

/* Sotto i 1100px le colonne non ci stanno: si passa a due per riga, e le
   etichette restano attaccate al loro campo. */
@media (max-width: 1100px) {
  .ride__voci { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .voce--vuota { display: none; }
  .voce--add { grid-column: 1 / -1; margin-left: 0; padding-left: 0; border-left: 0; }
}

.ride--tot { border-top: 1px solid var(--border-color); border-bottom: 0; font-weight: 600; }
.ride--tot .ride__what small { font-weight: 500; }
/* Nella riga dei totali le celle di quando e ore sono vuote: non devono
   occupare spazio quando la griglia si impila. */
.ride__when:empty, .ride__hours:empty { display: none; }

/* Stretto: quando sopra, descrizione sotto, ore e importo a destra; le voci
   restano una sotto l'altra invece di uscire dalla colonna. */
@media (max-width: 720px) {
  .ride__hd { grid-template-columns: minmax(0, 1fr) auto; }
  .ride__when { grid-column: 1 / -1; }
  .ride__hours { text-align: left; }
  .voce--add { margin-left: 0; }
}
.c6__sp { flex: 1; }

.c6__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c6__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c6__back:hover { border-color: var(--brand-violet); }
.c6__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c6__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--svc-fleet);
  padding: 3px 8px; border-radius: 6px;
}
.c6__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c6__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.seg {
  display: flex; gap: 2px; padding: 3px; border-radius: 10px;
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
}
.seg__b {
  border: 0; background: transparent; border-radius: 8px; cursor: pointer;
  padding: 6px 12px; font-size: 13px; font-family: inherit;
  color: var(--text-secondary);
}
.seg__b.is-on {
  background: var(--bg-primary); color: var(--text-primary); font-weight: 700;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.bar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin: 0 2px 12px;
}
.bar__sel { font-size: 13px; color: var(--text-tertiary); }

.tag {
  display: inline-block; padding: 3px 9px; border-radius: 999px;
  font-size: 12px; font-weight: 700; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
}
.tag--ok {
  color: #0f766e; background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.35);
}
.comp__r--liq .comp__v { color: #0f766e; font-weight: 700; }
.comp__r--sub .comp__k { padding-left: 14px; color: var(--text-secondary); }

.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}


.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.drv { display: flex; align-items: center; gap: 9px; }
.ava {
  width: 28px; height: 28px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 10.5px; font-weight: 700;
  color: #fff; background: var(--avatar-bg);
}
.drv__nm { font-weight: 600; color: var(--text-primary); }
.drv__ag { color: var(--text-tertiary); font-size: 11.5px; }
/* Le celle della tabella non vanno a capo: «TARIFF/A» e «Da liquida/re» si
   leggevano come errori. Le colonne sono dimensionate per starci. */
.board :deep(.el-table th .cell),
.board :deep(.el-table td .cell) { white-space: nowrap; }
.num { font-variant-numeric: tabular-nums; }
.val { font-weight: 700; }
.val--tot { color: var(--text-primary); }
/* Seconda riga di una cella della tabella: il residuo sotto l'importo. Non
   va a capo: «€ 24,97 da liquidar / e» si leggeva come un errore. */
.colsub {
  display: block; color: var(--text-tertiary); font-size: 11.5px; white-space: nowrap;
}

/* --------------------------------- dettaglio ---------------------------- */
.detail {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  padding: 16px; background: var(--bg-secondary);
}
@media (max-width: 860px) { .detail { grid-template-columns: 1fr; } }
.panel {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px; overflow: hidden;
}
.panel__h {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700; padding: 10px 13px;
  border-bottom: 1px solid var(--border-color); background: var(--bg-tertiary);
}
.panel__empty {
  margin: 0; padding: 13px; font-size: 12.5px; color: var(--text-tertiary);
  line-height: 1.5;
}
.panel__empty code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px;
  background: var(--bg-tertiary); padding: 0 4px; border-radius: 4px;
}

.comp { padding: 6px 13px 12px; }
.comp__r {
  display: flex; justify-content: space-between; gap: 10px; padding: 7px 0;
  border-bottom: 1px solid var(--border-color); font-size: 13px;
}
.comp__k { color: var(--text-secondary); }
.comp__k small {
  display: block; color: var(--text-tertiary); font-size: 11px;
}
.comp__v {
  font-variant-numeric: tabular-nums; font-weight: 650; color: var(--text-primary);
}
.comp__v.is-zero { color: var(--text-tertiary); font-weight: 500; }
.comp__r--tot {
  border-bottom: 0; border-top: 2px solid var(--border-color);
  margin-top: 2px; padding-top: 10px;
}
.comp__r--tot .comp__k { font-weight: 700; color: var(--text-primary); }
.comp__r--tot .comp__v { font-size: 16px; font-weight: 750; }

.panel--wide { grid-column: 1 / -1; }

.detail__actions {
  grid-column: 1 / -1; display: flex; gap: 8px; justify-content: flex-end;
}

.c6__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}
.c6__note code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px;
  background: var(--bg-tertiary); padding: 0 4px; border-radius: 4px;
}

.c6 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
